<template>
    <div>
        <Head :title="__(title)" />
      
        <div class="bg-white rounded-md shadow overflow-hidden mr-2">
            <div class="w-full mx-auto">
                <div class="pt-3 pl-6 border-b border-gray-200 tab-head">
                    <ul class="flex flex-wrap -mb-px" role="tablist">
                        <li class="mr-2" role="presentation" v-for="(tab, ti) in tabs" :key="ti">
                            <!-- Tabs normales -->
                            <div v-if="ti !== 2" 
                                class="inline-block py-4 px-4 text-sm font-medium text-center rounded-t-lg border-b-2 cursor-pointer transition-colors"
                                :class="{
                                    'text-indigo-600 border-indigo-600': activeTabIndex === ti,
                                    'text-gray-500 hover:text-gray-600 border-transparent hover:border-gray-300': activeTabIndex !== ti
                                }"
                                @click="setActiveTab(ti)">
                                {{ tab.name }}
                            </div>
                            
                            <!-- Datepicker para "Por Fechas" -->
                            <Datepicker v-if="ti === 2"
                                    calendar-class-name="schedule__picker"
                                    v-model="rangeDate"
                                    range
                                    multi-calendars
                                    :month-change-on-scroll="false"
                                    :enable-time-picker="false"
                                    ref="dateOverride"
                                    month-name-format="long"
                                    auto-apply
                                    keep-action-row
                                    :close-on-auto-apply="false"
                                    model-type="yyyy-MM-dd"
                                    @update:modelValue="selectRange">
                                <template #trigger>
                                    <div class="inline-block py-4 px-4 text-sm font-medium text-center rounded-t-lg border-b-2 cursor-pointer transition-colors"
                                        :class="{
                                            'text-indigo-600 border-indigo-600': activeTabIndex === ti && rangeDate,
                                            'text-gray-500 hover:text-gray-600 border-transparent hover:border-gray-300': activeTabIndex !== ti || !rangeDate
                                        }">
                                        {{ tab.name }}
                                    </div>
                                </template>
                                <template #action-select>
                                    <div class="flex cancel__apply w-full gap-4 p-6" v-if="rangeDate">
                                        <div class="flex __btn" @click="closeOverrideCalendar">Cancelar</div>
                                        <div class="flex __btn" @click="selectRange">Aplicar</div>
                                    </div>
                                </template>
                            </Datepicker>
                        </li>
                    </ul>
                </div>

                <div class="tab-content">
                    <div class="t-content active">
                        <div class="flex flex-col w-full">
                            <!-- Filtros adicionales -->
                            <div class="flex flex-col md:flex-row justify-between items-start md:items-center p-4 border-b gap-4">
                                <SearchInput 
                                    v-model="form.search" 
                                    class="w-full md:w-1/3" 
                                    :placeholder="__('Buscar eventos...')" 
                                    @reset="reset" />
                                
                                <div class="flex flex-col md:flex-row w-full md:w-2/3 gap-4">
                                    <!-- Filtro por tipo de envío -->
                                    <select 
                                        v-model="form.event_type" 
                                        class="form-select text-sm border-gray-300 rounded-md">
                                        <option :value="null">Todos los tipos de envío</option>
                                        <option value="cdmx">Entregas CDMX</option>
                                        <option value="edomex">Entregas EDO MEX</option>
                                    </select>
                                    
                                    <!-- Filtro por status -->
                                    <select 
                                        v-model="form.status" 
                                        class="form-select text-sm border-gray-300 rounded-md">
                                        <option :value="null">Todos los estados</option>
                                        <option value="por_atender">Por atender</option>
                                        <option value="por_enviar">Por enviar</option>
                                        <option value="enviado">Enviado</option>
                                    </select>
                                </div>
                            </div>

                            <!-- Lista de eventos -->
                            <div v-if="Object.keys(event_dates).length" class="flex flex-col w-full">
                                <div v-for="(ed_value, ed_key) in event_dates" :key="ed_key">
                                    <div class="p-4 flex e__date border-t bg-gray-50 items-center">
                                        <span class="font-medium">
                                            {{ moment(ed_key, 'YYYY-MM-DD').format('dddd, MMMM D, YYYY') }}
                                        </span>
                                        <span v-if="moment().isSame(ed_key, 'day')" class="ml-2 px-2 py-1 text-xs bg-indigo-100 text-indigo-800 rounded">
                                            TODAY
                                        </span>
                                    </div>
                                    
                                    <div class="flex flex-col event__item bg-white border-t w-full" v-for="event_date in ed_value" :key="event_date.id">
                                        <div class="px-4 py-5 flex gap-3 cursor-pointer select-none w-full" @click="event_date.have_details = !event_date.have_details">
                                            <div class="flex">
                                                <div class="w-5 h-5 rounded-full" :style="{'background-color': event_date.color}"></div>
                                            </div>
                                            <div class="flex" :class="{'line-through':!event_date.is_active}">
                                                {{ event_date.start }} - {{ event_date.end }}
                                            </div>
                                            <div class="flex flex-col pl-0 md:pl-8 gap-3" :class="{'line-through':!event_date.is_active}">
                                                <div class="name font-medium">{{ event_date.name }}</div>
                                                <div class="type text-sm text-gray-600">
                                                    Tipo de Envío: <strong>{{ event_date.event_type.name }}</strong>
                                                    <span class="ml-2 px-2 py-1 text-xs rounded capitalize"
                                                        :class="{
                                                            'bg-yellow-100 text-yellow-800': event_date.status === 'por_atender',
                                                            'bg-blue-100 text-blue-800': event_date.status === 'por_enviar',
                                                            'bg-green-100 text-green-800': event_date.status === 'enviado'
                                                        }">
                                                        {{ event_date.status }}
                                                    </span>
                                                </div>
                                            </div>
                                            <div class="flex ml-auto items-center">
                                                <div class="flex gap-3 details text-sm text-gray-500">
                                                    <Icon :name="event_date.have_details?'up':'down'" class="w-4 h-4 opacity-50" />
                                                    Detalles
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <!-- Detalles del evento -->
                                        <div class="px-4 py-5 flex flex-col md:flex-row gap-6 w-full bg-gray-50" v-if="event_date.have_details">
                                            <div class="flex flex-col gap-3 md:w-1/4">
                                                <Link 
                                                    :href="route('editevent.edit1', event_date.uid)" 
                                                    class="btn btn-indigo flex gap-3 items-center" 
                                                    type="button">
                                                    <Icon name="repeat" class="fill-white w-4" /> 
                                                    Reagendar
                                                </Link>
                                                <button 
                                                    v-if="event_date.is_active"
                                                    @click="cancelEvent(event_date.id)" 
                                                    class="btn btn-red flex gap-3 items-center" 
                                                    type="button">
                                                    <Icon name="trash" class="fill-white w-4" /> 
                                                    Cancelar
                                                </button>
                                                <Link 
                                                    :href="route('event_types.edit', event_date.event_type.id)" 
                                                    class="flex gap-2 pt-3 border-t items-center text-sm text-gray-600 hover:text-gray-900">
                                                    <Icon name="edit" class="h-4 w-4" /> 
                                                    Editar tipo de envío
                                                </Link>
                                            </div>
                                            
                                            <div class="flex flex-col gap-3 md:w-3/4">
                                                <div>
                                                    <div class="font-bold uppercase text-sm text-gray-500">Email</div>
                                                    <div>{{ event_date.email }}</div>
                                                </div>
                                                
                                                <div>
                                                    <div class="font-bold uppercase text-sm text-gray-500 pt-4">Instrucciones repartidor</div>
                                                    <div>{{ event_date.question }}</div>
                                                </div>
                                                
                                                <p class="text-sm text-gray-400 pt-4">
                                                    Creado {{ moment(event_date.created_at).format('D MMMM YYYY') }}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <!-- Estado vacío -->
                            <div v-else class="flex flex-col w-full p-8 text-center text-gray-500">
                                <p>{{ __('No se encontraron eventos para el periodo seleccionado.') }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- Paginación -->
            <div class="px-8 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-center">
                <Pagination :links="events.links" />
            </div>
        </div>
    </div>
</template>





<script>
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import Pagination from '@/Shared/Pagination'
import SearchInput from '@/Shared/SearchInput'
import { Link, Head } from '@inertiajs/inertia-vue3'
import moment_timezone from 'moment-timezone'
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css'
import { ref } from 'vue'

export default {
    metaInfo: { title: 'Eventos' },
    components: {
        Icon,
        Pagination,
        SearchInput,
        Link,
        Head,
        Datepicker,
    },
    layout: Layout,
    props: {
        filters: Object,
        events: Object,
        title: String,
    },
   data() {
    return {
        isReloading: false,
        filterChangeTimeout: null, // Para debounce de cambios en filtros
        tabs: [
            {'name': 'Nuevos', 'period': 'upcoming'},
            {'name': 'Pasados', 'period': 'past'},
            {'name': 'Por Fechas', 'period': 'range'},
            {'name': 'Cancelados', 'period': 'canceled'},
        ],
        activeTabIndex: 0,
        form: {
            search: this.filters?.search || null,
            event_type: this.filters?.event_type || null,
            status: this.filters?.status || null,
            period: this.filters?.period || 'upcoming',
            range: this.filters?.range || null,
        },
        event_dates: {},
        rangeDate: null,
        moment: moment_timezone
    }
},

// Modificar los watchers para evitar ejecuciones múltiples
watch: {
    'form.period'(newVal, oldVal) {
        if (newVal !== oldVal) {
            if (newVal === 'upcoming' || newVal === 'past') {
                this.setDefaultRange(newVal);
                // El watcher de range se encargará de llamar a applyFilters
            } else if (newVal !== 'range') {
                this.debounceApplyFilters();
            }
        }
    },
    'form.range': {
        deep: true,
        handler(newVal, oldVal) {
            if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
                this.debounceApplyFilters();
            }
        }
    },
    'form.event_type'() {
        this.debounceApplyFilters();
    },
    'form.status'() {
        this.debounceApplyFilters();
    },
    'form.search'() {
        this.debounceApplyFilters();
    }
},

// Métodos corregidos
methods: {
    // Nuevo método para debounce de filtros
    debounceApplyFilters() {
        if (this.filterChangeTimeout) {
            clearTimeout(this.filterChangeTimeout);
        }
        this.filterChangeTimeout = setTimeout(() => {
            this.applyFilters();
        }, 300); // Debounce de 300ms
    },

    // Método corregido para aplicar filtros
    applyFilters() {
        if (this.isReloading) return;
        
        this.isReloading = true;
        
        const filters = pickBy({
            search: this.form.search,
            event_type: this.form.event_type,
            status: this.form.status,
            period: this.form.period,
            range: this.form.period === 'range' ? this.form.range : null
        });

        this.$inertia.get(this.route('schedules.list'), filters, {
            preserveState: true,
            replace: true,
            onSuccess: () => {
                this.isReloading = false;
                this.processData();
            },
            onError: () => {
                this.isReloading = false;
            }
        });
    },

    // Método corregido para cambiar de pestaña
    setActiveTab(index) {
        if (this.isReloading) return;
        
        this.activeTabIndex = index;
        const selectedPeriod = this.tabs[index].period;
        
        if (this.form.period !== selectedPeriod) {
            this.form.period = selectedPeriod;
            
            if (selectedPeriod === 'upcoming' || selectedPeriod === 'past') {
                this.setDefaultRange(selectedPeriod);
            } else {
                // Para otros tipos como 'canceled' o 'range'
                this.applyFilters();
            }
        }
    },

    setDefaultRange(period) {
        const today = this.moment();
        let start, end;
        
        if (period === 'upcoming') {
            start = today.format('YYYY-MM-DD');
            end = today.clone().add(1, 'month').format('YYYY-MM-DD');
        } else if (period === 'past') {
            start = today.clone().subtract(1, 'month').format('YYYY-MM-DD');
            end = today.format('YYYY-MM-DD');
        }
        
        if (start && end) {
            this.form.range = { start, end };
        }
    },

    selectRange() {
        if (this.rangeDate && this.rangeDate.length === 2) {
            this.form.range = {
                start: this.moment(this.rangeDate[0]).format('YYYY-MM-DD'),
                end: this.moment(this.rangeDate[1]).format('YYYY-MM-DD')
            };
            this.form.period = 'range';
            this.closeOverrideCalendar();
            // El watcher de form.range aplicará los filtros
        }
    },

    closeOverrideCalendar() {
        // Implementar según la lógica de tu aplicación
        if (this.$refs.dateOverride) {
            this.$refs.dateOverride.closeMenu();
        }
    },

    reset() {
        this.form = {
            search: null,
            event_type: null,
            status: null,
            period: 'upcoming',
            range: null
        };
        this.activeTabIndex = 0;
        this.setDefaultRange('upcoming');
        // Los watchers se encargarán de aplicar los filtros
    },

    processData() {
        this.event_dates = {};
        
        if (!this.events?.data) return;

        this.events.data.forEach(event_item => {
            if (!event_item.date) return;
            
            if (!this.event_dates[event_item.date]) {
                this.event_dates[event_item.date] = [];
            }
            this.event_dates[event_item.date].push({
                ...event_item,
                have_details: false // Inicializamos la propiedad para la UI
            });
        });
    },

    cancelEvent(id) {
        if (confirm('¿Estás seguro de que deseas cancelar este evento?')) {
            this.axios.post(this.route('schedule.cancel', id))
                .then(() => {
                    // Usar Inertia para recargar solo los datos necesarios
                    this.$inertia.reload({ only: ['events'] });
                })
                .catch(console.error);
        }
    }
},
    mounted() {
        if (this.form.period === 'range' && this.form.range) {
            this.rangeDate = [
                new Date(this.form.range.start),
                new Date(this.form.range.end)
            ];
        }
        
        this.processData();
    },
    created() {
        // Configurar rango inicial si es necesario
        if (this.form.period === 'upcoming' || this.form.period === 'past') {
            this.setDefaultRange(this.form.period);
        }
        
        // Sincronizar pestaña activa
        this.activeTabIndex = this.tabs.findIndex(tab => tab.period === this.form.period);
        if (this.activeTabIndex === -1) this.activeTabIndex = 0;
    }
}
</script>